import { useState, useEffect } from 'react';
import { Box, IconButton, Flex, useColorModeValue, Image, Heading, Text, HStack, Icon, Card } from '@chakra-ui/react'; // Import Card
import { ArrowLeft, ArrowRight, MapPin, ImageSquare } from '@phosphor-icons/react'; // Add ImageSquare for fallback
import { Link as RouterLink } from 'react-router-dom';

// Helper to check if URL is valid
const isValidImageUrl = (url) => url && typeof url === 'string' && url.match(/^https?:\/\/.+/);

export default function SiteCarousel({ sites }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false); // Start with autoplay off
  const [imageError, setImageError] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [isHovering, setIsHovering] = useState(false);

  // Load current image and enable autoplay
  useEffect(() => {
    if (sites?.[currentIndex]) {
      const current = getPrimaryMedia(sites[currentIndex]);
      if (current?.url) {
        // Immediately mark the current image as preloaded
        setPreloadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(current.url);
          return newSet;
        });
        
        // Then handle delayed autoplay
        const timer = setTimeout(() => setAutoPlay(true), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [sites]);

  // When sites data first arrives, preload first few images
  useEffect(() => {
    if (!sites?.length) return;

    // Immediately mark the first image as preloaded so it shows right away
    const firstImage = getPrimaryMedia(sites[0]);
    if (firstImage?.url) {
      setPreloadedImages(prev => {
        const newSet = new Set(prev);
        newSet.add(firstImage.url);
        return newSet;
      });
    }

    // Preload next few images in the background
    const preloadInitialImages = async () => {
      const imagesToPreload = sites.slice(1, 4).map(site => getPrimaryMedia(site));
      try {
        // Use timeout to delay preloading of other images after first one is shown
        setTimeout(() => {
          imagesToPreload.forEach(media => {
            if (media?.url) {
              preloadImage(media.url).catch(err => {
                console.warn(`Background preload error for ${media?.url}:`, err);
              });
            }
          });
        }, 500);
      } catch (error) {
        console.error("Error in initial preload batch:", error);
      }
    };

    preloadInitialImages();
  }, [sites?.length > 0]);

  // Use semantic tokens for colors
  const arrowColor = useColorModeValue('text.default', 'text.default'); // Use default text color for arrows
  const dotInactiveColor = useColorModeValue('border.light', 'border.light'); // Use semantic token
  const dotActiveColor = useColorModeValue('interactive.primary', 'interactive.primary'); // Use semantic token
  const dotHoverColor = useColorModeValue('interactive.hover', 'interactive.hover'); // Use semantic token
  const arrowBg = useColorModeValue('whiteAlpha.600', 'blackAlpha.400'); // Use alpha backgrounds for better blending
  const arrowHoverBg = useColorModeValue('whiteAlpha.800', 'blackAlpha.600'); // Darken/lighten on hover

  // Find best available image with fallbacks
  const getPrimaryMedia = (site) => {
    if (!site) return { url: '/textures/parchment.png', type: 'image' };

    // 1. Try mainImage field first
    if (site.mainImage && isValidImageUrl(site.mainImage)) {
      return { url: site.mainImage, type: 'image' };
    }

    // 2. Try primaryMediaId if specified
    if (site.primaryMediaId) {
      const media = site.media?.find((m) => m.id === site.primaryMediaId);
      if (media && isValidImageUrl(media.url)) {
        return media;
      }
    }

    // 3. Try first media item with valid URL
    const validMedia = site.media?.filter((m) => m && isValidImageUrl(m.url));
    if (validMedia?.length > 0) {
      return validMedia[0];
    }

    // 4. Fallback to placeholder
    return { url: '/textures/parchment.png', type: 'image' };
  };

  // Function to preload an image
  const preloadImage = (url) => {
    if (!url || typeof window === 'undefined') {
      return Promise.resolve();
    }
    
    // Skip if already preloaded successfully
    if (preloadedImages.has(url)) {
      return Promise.resolve(url);
    }

    // Create a promise to handle image loading
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      
      img.onload = () => {
        setPreloadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(url);
          return newSet;
        });
        resolve(url);
      };

      img.onerror = (error) => {
        console.error(`Failed to preload image: ${url}`, error);
        // Don't completely reject - use our fallback strategy
        setImageError(true);
        // Still resolve so the promise chain continues
        resolve();
      };

      img.src = url; // Set src after adding event listeners
    }).catch(() => {
      // If image fails to load for any reason, mark as error and continue
      console.warn(`Image preload error handled for: ${url}`);
      setImageError(true);
    });
  };

  // Preload adjacent images with error handling
  useEffect(() => {
    if (!sites?.length || typeof window === 'undefined') return;

    const preloadAdjacent = async () => {
      try {
        const nextIndex = (currentIndex + 1) % sites.length;
        const prevIndex = (currentIndex - 1 + sites.length) % sites.length;

        const nextImage = getPrimaryMedia(sites[nextIndex]);
        const prevImage = getPrimaryMedia(sites[prevIndex]);

        // Use a timeout to stagger preloading requests and reduce server load
        setTimeout(() => {
          if (nextImage?.url) {
            preloadImage(nextImage.url).catch(() => {
              // Failure to preload shouldn't break the carousel
              console.warn(`Failed to preload next image: ${nextImage.url}`);
            });
          }
        }, 300);

        setTimeout(() => {
          if (prevImage?.url) {
            preloadImage(prevImage.url).catch(() => {
              console.warn(`Failed to preload previous image: ${prevImage.url}`);
            });
          }
        }, 600);
      } catch (error) {
        console.error('Error in preloading process:', error);
      }
    };

    preloadAdjacent();
  }, [currentIndex, sites, typeof window !== 'undefined']);

  // Auto-advance slides with pause on hover
  useEffect(() => {
    if (!autoPlay || !sites?.length || isHovering) return;

    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % sites.length);
    }, 7000); // Increased interval to reduce server load

    return () => clearTimeout(timer);
  }, [currentIndex, autoPlay, sites, isHovering]);

  // Reset image error state when slide changes
  useEffect(() => {
    setImageError(false);
  }, [currentIndex]);

  // Navigation functions
  const goToPrev = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + sites.length) % sites.length);
  };

  const goToNext = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % sites.length);
  };

  const goToSlide = (index) => {
    setAutoPlay(false);
    setCurrentIndex(index);
  };

  if (!sites?.length) return null;

  const currentSite = sites[currentIndex];
  const primaryMedia = getPrimaryMedia(currentSite);
  const slideBg = useColorModeValue('bg.surface', 'bg.surface'); // Use theme token

  return (
    // Use Card component with journal variant for consistency
    <Card 
      variant="journal" 
      position="relative" 
      height="100%" /* Use 100% height to respect parent container */
      borderRadius="lg" 
      overflow="hidden" 
      p={0}
    >
      {/* Current Slide */}
      <Box 
        height="100%" 
        width="100%" 
      bg={slideBg}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      opacity={1} /* Always show the container */
      transition="opacity 0.5s ease-in-out"
      >
        {/* Directly render the image and site info */}
        <Box
          position="relative"
          height="100%"
          width="100%"
          overflow="hidden"
          as={RouterLink}
          to={`/site/${currentSite.id}`}
          display="block" // Ensure Box behaves like a link container
          _hover={{ textDecoration: 'none' }} // Remove underline on hover
        >
          {/* Image with Loading States */}
          <Box position="relative" width="100%" height="100%" bg="bg.surface" key={`carousel-image-${currentIndex}`}>
            {imageError ? (
              <Flex
                width="100%"
                height="100%"
                align="center"
                justify="center"
                bg="bg.accent"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  bgImage: 'var(--texture-aged)',
                  opacity: 0.1,
                  bgRepeat: 'repeat',
                  bgSize: '150px',
                  bgBlendMode: 'multiply',
                  pointerEvents: 'none',
                }}
              >
                <Icon as={ImageSquare} boxSize="50px" color="text.muted" />
              </Flex>
            ) : (
              <>
                {!preloadedImages.has(primaryMedia.url) && !imageError && (
                  <Flex
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    align="center"
                    justify="center"
                    bg="bg.surface"
                  >
                    <Icon as={ImageSquare} boxSize="50px" color="text.muted" />
                  </Flex>
                )}
                <Image
                  src={primaryMedia.url}
                  alt={currentSite.name}
                  width="100%"
                  height="100%"
                  onError={() => setImageError(true)}
                  objectFit="cover"
                  objectPosition="center"
                  fallbackSrc="/textures/parchment.png"
                  loading="eager" /* Change to eager loading for first slide */
                  opacity={preloadedImages.has(primaryMedia.url) || imageError ? 1 : 0}
                  transition="opacity 0.3s ease-in-out"
                />
              </>
            )}
          </Box>

          {/* Site Info Overlay - Enhanced for mobile */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            p={{ base: 3, md: 4 }} // Reduced padding on mobile
            background="linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.4), transparent)" // Darkened gradient for better readability
            color="white"
            zIndex="1" // Ensure overlay is above other elements
          >
            {/* Mobile-optimized heading */}
            <Heading 
              size={{ base: "sm", md: "md" }} // Smaller on mobile
              mb={1} 
              noOfLines={1} 
              fontFamily="heading" 
              color="white"
              fontWeight="semibold" // Slightly bolder for better visibility
            >
              {currentSite.name}
            </Heading>
            {currentSite.description && (
              <Text 
                fontSize={{ base: "xs", md: "sm" }} // Smaller on mobile
                noOfLines={{ base: 1, md: 2 }} // Only 1 line on mobile to save space
                mb={{ base: 1, md: 2 }} // Reduced margin on mobile
                fontFamily="body"
              >
                {currentSite.description}
              </Text>
            )}
            {currentSite.location?.address && (
              <HStack spacing={1} alignItems="center" mt={{ base: 0, md: 1 }}> 
                <Icon as={MapPin} boxSize={{ base: 2.5, md: 3 }} />
                <Text 
                  fontSize={{ base: "2xs", md: "xs" }} // Smaller on mobile
                  noOfLines={1} 
                  fontFamily="body"
                >
                  {(currentSite.location.region && currentSite.location.country)
                    ? `${currentSite.location.region}, ${currentSite.location.country}`
                    : currentSite.location.country || currentSite.location.region || currentSite.location.address}
                </Text>
              </HStack>
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Arrows - Enhanced for mobile */}
      <IconButton
        icon={<ArrowLeft weight="bold" size={30} />}
        onClick={goToPrev}
        position="absolute"
        left={{ base: "10px", md: "15px" }}
        top="50%"
        transform="translateY(-50%)"
        zIndex="10"
        color="white"
        bg="transparent"
        _hover={{ bg: "blackAlpha.300" }}
        aria-label="Previous slide"
        variant="ghost"
        borderRadius="md" // Use slight rounding for hover state
        w={{ base: "50px", md: "45px" }}
        h={{ base: "50px", md: "45px" }}
        boxShadow="none" // Remove shadow
        textShadow="0 1px 3px rgba(0,0,0,0.6)" // Add shadow to icon itself
        _active={{ 
          transform: "translateY(-50%) scale(0.95)",
          bg: "blackAlpha.400"
        }}
      />
      <IconButton
        icon={<ArrowRight weight="bold" size={30} />}
        onClick={goToNext}
        position="absolute"
        right={{ base: "10px", md: "15px" }}
        top="50%"
        transform="translateY(-50%)"
        zIndex="10"
        color="white"
        bg="transparent"
        _hover={{ bg: "blackAlpha.300" }}
        aria-label="Next slide"
        variant="ghost"
        borderRadius="md" // Use slight rounding for hover state
        w={{ base: "50px", md: "45px" }}
        h={{ base: "50px", md: "45px" }}
        boxShadow="none" // Remove shadow
        textShadow="0 1px 3px rgba(0,0,0,0.6)" // Add shadow to icon itself
        _active={{ 
          transform: "translateY(-50%) scale(0.95)",
          bg: "blackAlpha.400"
        }}
      />

      {/* Breadcrumb Indicators */}
      <Flex
        position="absolute"
        bottom="10px" // Position from the bottom
        left="50%" // Center horizontally
        transform="translateX(-50%)" // Adjust for centering
        zIndex="10" // Ensure it's above the image overlay gradient
        alignItems="center"
        justifyContent="center"
        px={2} // Add some padding
        py={1}
        bg="blackAlpha.500" // Semi-transparent background
        borderRadius="md" // Rounded corners
        color="white" // Text color
        fontSize="xs" // Small font size
        fontFamily="body"
      >
        <Text fontWeight="medium">{currentIndex + 1}</Text>
        <Text mx={1}>/</Text>
        <Text>{sites.length}</Text>
      </Flex>
    </Card>
  );
}
